import { __assign, __makeTemplateObject, __read, __rest } from "tslib";
import React, { forwardRef, useMemo, useState } from 'react';
import { combineProps, hasValue, logInDev, useControlled, styled, } from '../../../../foundation';
import { RcButton } from '../../../Buttons';
import { RcMenu } from '../../../Menu';
import { RcVirtualizedMenu } from '../../../VirtualizedMenu';
import { RcSelect } from '../Select';
import { SelectArrowDownIcon } from '../styles';
import { plainSelectStyle } from './styles';
import { RcPlainSelectInputClasses, RcPlainSelectTouchRippleClasses, switchVariantToButtonVariant, } from './utils';
var defaultButtonProps = {
    TouchRippleProps: { classes: RcPlainSelectTouchRippleClasses },
};
var EmptyIcon = function () { return null; };
var displayName = 'RcPlainSelect';
var plainAnchorOrigin = {
    horizontal: 'left',
    vertical: 'bottom',
};
var _RcPlainSelect = forwardRef(function (_a, ref) {
    var children = _a.children, className = _a.className, classes = _a.classes, valueProp = _a.value, defaultValue = _a.defaultValue, renderValue = _a.renderValue, variant = _a.variant, ButtonProps = _a.ButtonProps, innerRef = _a.innerRef, SelectInputProps = _a.SelectInputProps, InputProps = _a.InputProps, disabled = _a.disabled, autoFocus = _a.autoFocus, size = _a.size, virtualize = _a.virtualize, fullWidth = _a.fullWidth, color = _a.color, 
    //
    IconComponent = _a.IconComponent, onOpenProp = _a.onOpen, onCloseProp = _a.onClose, onChangeProp = _a.onChange, rest = __rest(_a, ["children", "className", "classes", "value", "defaultValue", "renderValue", "variant", "ButtonProps", "innerRef", "SelectInputProps", "InputProps", "disabled", "autoFocus", "size", "virtualize", "fullWidth", "color", "IconComponent", "onOpen", "onClose", "onChange"]);
    var _b = __read(useControlled({
        controlled: valueProp,
        default: defaultValue,
        name: displayName,
    }), 2), value = _b[0], setValue = _b[1];
    var _c = __read(useState(false), 2), open = _c[0], setOpen = _c[1];
    var isEmpty = !hasValue(value);
    if (process.env.NODE_ENV !== 'production' && isEmpty) {
        logInDev({
            component: displayName,
            message: '[Juno]: Plain Select must have init value',
            level: 'error',
        });
    }
    var display = useMemo(function () {
        var item = children.find(function (child) {
            return child.props['value'] === value;
        });
        return item === null || item === void 0 ? void 0 : item.props['children'];
    }, [children, value]);
    var _renderValue = function (newValue) {
        var _variant = switchVariantToButtonVariant(variant);
        return (React.createElement(RcButton, __assign({ ref: ref, innerRef: innerRef, disabled: disabled, size: size, color: color, fullWidth: fullWidth, autoFocus: autoFocus, variant: _variant, "aria-label": "open menu", "aria-haspopup": "listbox" }, combineProps(defaultButtonProps, ButtonProps)),
            renderValue ? renderValue(newValue) : display,
            (IconComponent && React.createElement(IconComponent, { open: open })) || (React.createElement(SelectArrowDownIcon
            // * reset default color
            , { 
                // * reset default color
                color: undefined, 
                // *for reverse button
                className: open ? 'MuiSelect-iconOpen' : '' }))));
    };
    var _SelectInputProps = useMemo(function () {
        return combineProps({
            MenuComponent: virtualize ? RcVirtualizedMenu : RcMenu,
            MenuProps: {
                anchorOrigin: plainAnchorOrigin,
            },
            SelectDisplayProps: {
                // * clean default select tabIndex set,
                // * use our custom button render to trigger default event
                tabIndex: undefined,
                'aria-haspopup': undefined,
                role: undefined,
            },
        }, SelectInputProps);
    }, [SelectInputProps, virtualize]);
    var _InputProps = useMemo(function () {
        return combineProps({
            classes: RcPlainSelectInputClasses,
            disableUnderline: true,
        }, InputProps);
    }, [InputProps]);
    return (React.createElement(RcSelect, __assign({ className: className, value: value, 
        // * set any for select could not find any class
        variant: 'none', disabled: disabled, IconComponent: EmptyIcon, onOpen: function (e) {
            setOpen(true);
            onOpenProp === null || onOpenProp === void 0 ? void 0 : onOpenProp(e);
        }, onClose: function (e) {
            setOpen(false);
            onCloseProp === null || onCloseProp === void 0 ? void 0 : onCloseProp(e);
        }, onChange: function (e, v) {
            setValue(e.target.value);
            onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(e, v);
        }, SelectInputProps: _SelectInputProps, InputProps: _InputProps, renderValue: _renderValue, fullWidth: fullWidth, color: color }, rest), children));
});
/** @release */
var RcPlainSelect = styled(_RcPlainSelect)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), plainSelectStyle);
RcPlainSelect.defaultProps = {
    variant: 'text',
    size: 'medium',
};
RcPlainSelect.displayName = displayName;
export { RcPlainSelect };
var templateObject_1;
